<template>
  <q-card>
    <q-card-section class="bg-grey-2">
      <div class="text-h6">Letter Generation History</div>
    </q-card-section>

    <q-card-section>
      <q-table
        :rows="generations"
        :columns="columns"
        row-key="id"
        :loading="loading"
        flat
        :rows-per-page-options="[10, 25, 50]"
      >
        <template #body="props">
          <q-tr :props="props">
            <q-td key="generated_at" :props="props">
              {{ formatDate(props.row.generated_at) }}
            </q-td>
            <q-td key="type" :props="props">
              <q-badge :color="props.row.generation_type === 'draft' ? 'orange' : 'blue'">
                {{ props.row.generation_type.toUpperCase() }}
              </q-badge>
            </q-td>
            <q-td key="recipients" :props="props">
              {{ props.row.email_logs?.length || 0 }} recipient(s)
            </q-td>
            <q-td key="status" :props="props">
              <q-badge :color="getStatusColor(props.row.email_status)">
                {{ props.row.email_status || 'N/A' }}
              </q-badge>
            </q-td>
            <q-td key="actions" :props="props">
              <q-btn
                flat
                dense
                icon="download"
                @click="downloadPdf(props.row)"
              >
                <q-tooltip>Download PDF</q-tooltip>
              </q-btn>
              <q-btn
                v-if="props.row.email_logs?.length"
                flat
                dense
                icon="email"
                @click="props.expand = !props.expand"
              >
                <q-tooltip>View Email Details</q-tooltip>
              </q-btn>
            </q-td>
          </q-tr>

          <!-- Expandable: Email details -->
          <q-tr v-show="props.expand" :props="props">
            <q-td colspan="100%">
              <div class="q-pa-md bg-grey-1">
                <div class="text-subtitle2 q-mb-sm">Email Delivery Details:</div>
                <q-list dense bordered>
                  <q-item v-for="log in props.row.email_logs" :key="log.id">
                    <q-item-section>
                      <q-item-label>{{ log.contractor?.company_name || log.contractor?.business_name || log.contractor?.name }}</q-item-label>
                      <q-item-label caption>{{ log.email_address }}</q-item-label>
                    </q-item-section>
                    <q-item-section side>
                      <q-badge :color="getStatusColor(log.email_status)">
                        {{ log.email_status }}
                      </q-badge>
                    </q-item-section>
                    <q-item-section side>
                      <div v-if="log.acknowledged_at" class="text-positive">
                        <q-icon name="check_circle" />
                        Acknowledged {{ formatDate(log.acknowledged_at) }}
                      </div>
                      <div v-else class="text-grey">
                        Not acknowledged
                      </div>
                    </q-item-section>
                  </q-item>
                </q-list>
              </div>
            </q-td>
          </q-tr>
        </template>
      </q-table>
    </q-card-section>
  </q-card>
</template>

<script setup>
  import { ref, onMounted } from 'vue';
  import { authenticatedFetch } from '@/utils/apiHelper';
  import { useQuasar } from 'quasar';

  const props = defineProps({
    tenderId: { type: Number, required: true },
  });

  const $q = useQuasar();

  const loading = ref(false);
  const generations = ref([]);

  const columns = [
    { name: 'generated_at', label: 'Generated', field: 'generated_at', align: 'left', sortable: true },
    { name: 'type', label: 'Type', field: 'generation_type', align: 'left' },
    { name: 'recipients', label: 'Recipients', align: 'left' },
    { name: 'status', label: 'Email Status', field: 'email_status', align: 'left' },
    { name: 'actions', label: 'Actions', align: 'center' },
  ];

  onMounted(() => {
    loadHistory();
  });

  async function loadHistory() {
    loading.value = true;
    try {
      // Use the generations endpoint for tender letter history
      const response = await authenticatedFetch(`/api/tenders/${props.tenderId}/generations`);
      const data = await response.json();

      if (data.success) {
        generations.value = data.data || [];
      } else {
        generations.value = [];
      }
    } catch (error) {
      if (!error.isAuthError) {
        $q.notify({
          type: 'negative',
          message: 'Failed to load generation history: ' + error.message,
        });
      }
    } finally {
      loading.value = false;
    }
  }

  function formatDate(dateString) {
    return new Date(dateString).toLocaleString('en-AU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function getStatusColor(status) {
    const colors = {
      pending: 'orange',
      sent: 'positive',
      failed: 'negative',
      bounced: 'negative',
    };
    return colors[status] || 'grey';
  }

  function downloadPdf(generation) {
    const token = sessionStorage.getItem('token') || localStorage.getItem('auth_token');

    // Use new download endpoint if generation has id/version
    if (generation.id || generation.version) {
      const version = generation.version || generation.id;
      window.open(`/api/tenders/${props.tenderId}/letter/download/${version}?token=${token}`, '_blank');
    } else {
      // Fallback to old endpoint
      const isDraft = generation.generation_type === 'draft' ? 1 : 0;
      window.open(`/api/tenders/${props.tenderId}/generate-letter?draft=${isDraft}&show_prices=1&token=${token}`, '_blank');
    }
  }

  defineExpose({ loadHistory });
</script>
